/**
 * @file  src/helpers/pendingImportSummary.js
 * @brief Pure display helpers for the "From Your MLS" pending-import cards.
 */

import { compactPrice } from "./compactPrice";

// Card title for a pending import. MIBOR saved searches usually carry a
// county but no city, so a city/state-only title left every card reading
// "Saved search".
export function pendingImportLocationSummary({
  city,
  county,
  state,
  proximityAddress,
} = {}) {
  const countyLabel = county
    ? /county$/i.test(county)
      ? county
      : `${county} County`
    : null;
  const place = [city, countyLabel, state].filter(Boolean).join(", ");
  return place || (proximityAddress ? `Near ${proximityAddress}` : "");
}

// Budget chip: "$500K+" / "Up to $700K" / "$500K–$700K". Null when the
// search named no price.
export function pendingImportBudgetChip(min, max) {
  if (min && max) {
    const low = compactPrice(min);
    const high = compactPrice(max);
    if (Number(min) === Number(max)) return low;
    // Both ends round to one label ("$1.3M–$1.3M"): say it is approximate
    // instead of showing a range that reads as a single price.
    return low === high ? `About ${low}` : `${low}–${high}`;
  }
  if (min) return `${compactPrice(min)}+`;
  if (max) return `Up to ${compactPrice(max)}`;
  return null;
}

// Property type ids the card can name (the platform's PROPERTY_TYPE_TO_CATEGORY
// registry). An id not listed here simply has no chip.
const TYPE_LABELS = {
  1: "Single-family",
  2: "Condo",
  3: "Townhome",
  5: "Manufactured",
  14: "Duplex",
  15: "Triplex",
  16: "Fourplex",
};

const formatNumber = (n) => Number(n).toLocaleString("en-US");

function rangeLabel(min, max, format, suffix = "") {
  if (min && max) return `${format(min)}–${format(max)}${suffix}`;
  if (min) return `${format(min)}+${suffix}`;
  if (max) return `Up to ${format(max)}${suffix}`;
  return null;
}

// The small facts shown as chips on a pending-import card. Only what the
// search actually said — nothing here is a guess.
export function pendingImportChips(pendingImport = {}) {
  const {
    minBdgt,
    maxBdgt,
    prptySubTypes,
    noBedRm,
    noBathRm,
    sqFtMin,
    sqFtMax,
    yrBltMin,
    yrBltMax,
    proximityMiles,
  } = pendingImport;
  const typeLabels = (Array.isArray(prptySubTypes) ? prptySubTypes : [])
    .map((id) => TYPE_LABELS[id])
    .filter(Boolean);
  const yearLabel = rangeLabel(yrBltMin, yrBltMax, String);
  return [
    typeLabels.length ? typeLabels.join(", ") : null,
    pendingImportBudgetChip(minBdgt, maxBdgt),
    noBedRm ? `${noBedRm}+ bed` : null,
    noBathRm ? `${noBathRm}+ bath` : null,
    rangeLabel(sqFtMin, sqFtMax, formatNumber, " sq ft"),
    yearLabel ? `Built ${yearLabel}` : null,
    proximityMiles ? `Within ${proximityMiles} mi` : null,
  ].filter(Boolean);
}

// Notes for the banner on the form, one per guess the API made.
export function pendingImportAssumptionNotes(assumed) {
  return (Array.isArray(assumed) ? assumed : [])
    .map((a) => a?.note)
    .filter(Boolean);
}
